import React, { useState, useEffect } from 'react'
import {
  PanelContainer,
  PanelHeader,
  PanelTitle,
  CloseButton,
  PanelBody,
  Section,
  SectionTitle,
  SectionDescription,
  FormGroup,
  Input,
  TextArea,
  CharCount,
  SaveButton,
  ResetButton,
  ButtonGroup,
  SuccessMessage,
  InfoBox,
  InfoTitle,
  InfoText,
} from './styled'
import { MapBranding } from '../../../types/map'

interface BrandingPanelProps {
  branding?: MapBranding
  isOpen: boolean
  isOwner: boolean
  onClose: () => void
  onSave: (branding: MapBranding) => void
}

export const BrandingPanel: React.FC<BrandingPanelProps> = ({
  branding,
  isOpen,
  isOwner,
  onClose,
  onSave,
}) => {
  const [title, setTitle] = useState(branding?.title || '')
  const [description, setDescription] = useState(branding?.description || '')
  const [details, setDetails] = useState(branding?.details || '')
  const [hasChanges, setHasChanges] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)

  useEffect(() => {
    setTitle(branding?.title || '')
    setDescription(branding?.description || '')
    setDetails(branding?.details || '')
    setHasChanges(false)
  }, [branding, isOpen])

  useEffect(() => {
    if (showSuccess) {
      const timer = setTimeout(() => setShowSuccess(false), 3000)
      return () => clearTimeout(timer)
    }
  }, [showSuccess])

  const handleInputChange = (
    field: 'title' | 'description' | 'details',
    value: string,
  ) => {
    setHasChanges(true)
    switch (field) {
      case 'title':
        setTitle(value)
        break
      case 'description':
        setDescription(value)
        break
      case 'details':
        setDetails(value)
        break
    }
  }

  const handleSave = () => {
    const updatedBranding: MapBranding = {
      title: title.trim(),
      description: description.trim(),
      details: details.trim(),
    }
    onSave(updatedBranding)
    setHasChanges(false)
    setShowSuccess(true)
  }

  const handleReset = () => {
    setTitle(branding?.title || '')
    setDescription(branding?.description || '')
    setDetails(branding?.details || '')
    setHasChanges(false)
  }

  if (!isOwner) {
    return null
  }

  return (
    <PanelContainer $isOpen={isOpen}>
      <PanelHeader>
        <PanelTitle>Branding Settings</PanelTitle>
        <CloseButton onClick={onClose}>&times;</CloseButton>
      </PanelHeader>

      <PanelBody>
        {showSuccess && (
          <SuccessMessage>Branding saved successfully!</SuccessMessage>
        )}

        <InfoBox>
          <InfoTitle>Customize Your Floorplan</InfoTitle>
          <InfoText>
            Add custom branding to make your floorplan more professional and
            informative. These fields will be displayed when viewing or sharing
            your map.
          </InfoText>
        </InfoBox>

        <Section>
          <SectionTitle>Floorplan Title</SectionTitle>
          <SectionDescription>
            Large text that appears prominently above the floorplan
          </SectionDescription>
          <FormGroup>
            <Input
              type="text"
              value={title}
              onChange={e => handleInputChange('title', e.target.value)}
              placeholder="e.g., Main Office Floor Plan"
              maxLength={100}
            />
            <CharCount>{title.length}/100</CharCount>
          </FormGroup>
        </Section>

        <Section>
          <SectionTitle>Floorplan Description</SectionTitle>
          <SectionDescription>
            A paragraph that provides context about the floorplan
          </SectionDescription>
          <FormGroup>
            <TextArea
              value={description}
              onChange={e => handleInputChange('description', e.target.value)}
              placeholder="e.g., This floor plan shows the layout of our main office building, including all meeting rooms, work areas, and common spaces."
              maxLength={500}
              rows={4}
            />
            <CharCount>{description.length}/500</CharCount>
          </FormGroup>
        </Section>

        <Section>
          <SectionTitle>Floorplan Details</SectionTitle>
          <SectionDescription>
            Fine print or additional information displayed below the floorplan
          </SectionDescription>
          <FormGroup>
            <TextArea
              value={details}
              onChange={e => handleInputChange('details', e.target.value)}
              placeholder="e.g., Last updated: December 2024 | Scale: 1:100 | Emergency exits marked in green"
              maxLength={300}
              rows={3}
            />
            <CharCount>{details.length}/300</CharCount>
          </FormGroup>
        </Section>

        <ButtonGroup>
          <SaveButton onClick={handleSave} disabled={!hasChanges}>
            Save Branding
          </SaveButton>
          <ResetButton onClick={handleReset} disabled={!hasChanges}>
            Reset Changes
          </ResetButton>
        </ButtonGroup>
      </PanelBody>
    </PanelContainer>
  )
}
